export const RUNNER_WIDTH = 600;
export const RUNNER_HEIGHT = 150;
export const GROUND_Y = 134;
export const CAR_X = 16;

// Hitbox of the car inside the 64x64 viewBox of the loader's VehicleOutline, including its 2px stroke.
export const CAR_BOX = { left: 4, right: 60, top: 22, bottom: 53 } as const;
export const DUCK_SCALE = 0.55;

const START_SPEED = 360;
const MAX_SPEED = 780;
const ACCELERATION = 6;
const GRAVITY = 2000;
const FAST_FALL_GRAVITY = 6000;
const JUMP_VELOCITY = 620;
const HIT_TOLERANCE = 3;
const BARRIER_MIN_SPEED = 480;

export type ObstacleKind = "cone" | "tires" | "barrier";

export interface Obstacle {
  kind: ObstacleKind;
  x: number;
  width: number;
  top: number;
  bottom: number;
  count: number;
}

export interface RunnerState {
  status: "ready" | "running" | "crashed";
  speed: number;
  distance: number;
  /** Height of the car above the ground. */
  y: number;
  velocity: number;
  ducking: boolean;
  obstacles: Obstacle[];
  untilNextObstacle: number;
}

export function createRunner(status: RunnerState["status"] = "running"): RunnerState {
  return {
    status,
    speed: START_SPEED,
    distance: 0,
    y: 0,
    velocity: 0,
    ducking: false,
    obstacles: [],
    untilNextObstacle: RUNNER_WIDTH,
  };
}

export function runnerScore(state: RunnerState): number {
  return Math.floor(state.distance / 40);
}

export function jump(state: RunnerState): void {
  if (state.status !== "running" || state.y > 0) return;
  state.velocity = JUMP_VELOCITY;
  state.y = Number.EPSILON;
}

function createObstacle(speed: number, random: () => number): Obstacle {
  if (speed >= BARRIER_MIN_SPEED && random() < 0.25) {
    // Hangs low enough that only a ducking car fits underneath.
    return { kind: "barrier", x: RUNNER_WIDTH, width: 44, top: 0, bottom: GROUND_Y - 19, count: 1 };
  }
  if (random() < 0.35) {
    return { kind: "tires", x: RUNNER_WIDTH, width: 26, top: GROUND_Y - 33, bottom: GROUND_Y, count: 3 };
  }
  const count = 1 + Math.floor(random() * (speed >= BARRIER_MIN_SPEED ? 3 : 2));
  return { kind: "cone", x: RUNNER_WIDTH, width: 16 * count, top: GROUND_Y - 24, bottom: GROUND_Y, count };
}

function carHitbox(state: RunnerState) {
  const bottom = GROUND_Y - state.y - HIT_TOLERANCE;
  const height = (CAR_BOX.bottom - CAR_BOX.top) * (state.ducking ? DUCK_SCALE : 1) - 2 * HIT_TOLERANCE;
  return {
    left: CAR_X + CAR_BOX.left + HIT_TOLERANCE,
    right: CAR_X + CAR_BOX.right - HIT_TOLERANCE,
    top: bottom - height,
    bottom,
  };
}

export function stepRunner(state: RunnerState, duck: boolean, dt: number, random: () => number = Math.random): void {
  if (state.status !== "running") return;

  state.speed = Math.min(MAX_SPEED, state.speed + ACCELERATION * dt);
  const dx = state.speed * dt;
  state.distance += dx;

  if (state.y > 0) {
    state.velocity -= (duck ? FAST_FALL_GRAVITY : GRAVITY) * dt;
    state.y = Math.max(0, state.y + state.velocity * dt);
    if (state.y === 0) state.velocity = 0;
  }
  state.ducking = duck && state.y === 0;

  for (const obstacle of state.obstacles) obstacle.x -= dx;
  state.obstacles = state.obstacles.filter((obstacle) => obstacle.x + obstacle.width > 0);

  state.untilNextObstacle -= dx;
  if (state.untilNextObstacle <= 0) {
    const obstacle = createObstacle(state.speed, random);
    state.obstacles.push(obstacle);
    // The gap grows with speed so every obstacle stays clearable after landing.
    const minGap = state.speed * 0.7 + 110;
    state.untilNextObstacle = obstacle.width + minGap + random() * minGap * 0.8;
  }

  const car = carHitbox(state);
  const hit = state.obstacles.some(
    (obstacle) =>
      car.left < obstacle.x + obstacle.width &&
      car.right > obstacle.x &&
      car.top < obstacle.bottom &&
      car.bottom > obstacle.top,
  );
  if (hit) state.status = "crashed";
}

const HIGH_SCORE_KEY = "alphartis.car-runner.v1";

export function readHighScore(): number {
  try {
    const value = Number(localStorage.getItem(HIGH_SCORE_KEY));
    return Number.isSafeInteger(value) && value > 0 ? value : 0;
  } catch {
    // Storage can be unavailable in privacy mode; the high score then lives in memory only.
    return 0;
  }
}

export function saveHighScore(score: number): void {
  try {
    localStorage.setItem(HIGH_SCORE_KEY, String(score));
  } catch {
    /* See readHighScore. */
  }
}
